import {
  ColumnDetail,
  DataQualityTagItem,
  SensitivityTagItem,
  TagItem,
  TagsStructure,
} from '@/hooks/api/use-table-details-query'
import {
  ENRICHMENT_AGENTS,
  ENRICHMENT_FIELD_MAPPINGS,
  ENRICHMENT_METADATA_TYPES,
  ENRICHMENT_TYPES,
  EnrichmentMetadataType,
  EnrichmentType,
  TAG_CATEGORIES,
  TagCategory,
} from './constants'
import { EditableFieldName, TableViewType, TaggingFlags } from './types'

// 👇 Enrichment Configuration

export const AGENT_OPTIONS = [
  {
    value: ENRICHMENT_AGENTS.EM_AGENTS,
    label: 'Generative Enrichment',
    isTranslated: true,
  },
  {
    value: ENRICHMENT_AGENTS.BANKING,
    label: 'Banking & Financial Services',
    isTranslated: false,
  },
  {
    value: ENRICHMENT_AGENTS.HEALTHCARE,
    label: 'Healthcare & Life Sciences',
    isTranslated: false,
  },
  {
    value: ENRICHMENT_AGENTS.RETAIL,
    label: 'Retail & Consumer Goods',
    isTranslated: false,
  },
  {
    value: ENRICHMENT_AGENTS.TELECOMMUNICATIONS,
    label: 'Telecommunications',
    isTranslated: false,
  },
  {
    value: ENRICHMENT_AGENTS.ENERGY,
    label: 'Energy & Utilities',
    isTranslated: false,
  },
  {
    value: ENRICHMENT_AGENTS.PUBLIC,
    label: 'Public Sector / Government',
    isTranslated: false,
  },
]

/**
 * Gets agent label for display
 */
export const getAgentLabel = (value: string): string => {
  if (value === ENRICHMENT_AGENTS.EM_AGENTS) return ''
  const agent = AGENT_OPTIONS.find((option) => option.value === value)
  return agent ? agent.label : value
}

interface MetadataSelectOption {
  value: EnrichmentMetadataType
  label: string
  disabled?: boolean
}

/**
 * Checks if tagging flags are incomplete (all flags are null)
 */
export const areAllTaggingFlagsNull = (taggingFlags?: TaggingFlags): boolean => {
  return (
    taggingFlags?.is_pii_tagged === null &&
    taggingFlags?.is_sensitivity_tagged === null &&
    taggingFlags?.is_dq_tagged === null
  )
}

/**
 * Checks if sensitivity and DQ tags are both null
 */
export const areSensitivityAndDQTagsNull = (taggingFlags?: TaggingFlags): boolean => {
  return taggingFlags?.is_sensitivity_tagged === null && taggingFlags?.is_dq_tagged === null
}

/**
 * Checks if DQ tags are null
 */
export const isDQTagsNull = (taggingFlags?: TaggingFlags): boolean => {
  return taggingFlags?.is_dq_tagged === null
}

/**
 * Gets metadata select options based on enrichment type and current view
 */
export const getMetadataSelectOptions = (
  type: EnrichmentType,
  hasEmptyMetadataFields: boolean = false,
  tableCurrentView: TableViewType = 'metadata',
  taggingFlags?: TaggingFlags,
): MetadataSelectOption[] => {
  if (type === ENRICHMENT_TYPES.TABLE) {
    return [
      {
        value: ENRICHMENT_METADATA_TYPES.TABLE_DESCRIPTION,
        label: 'Table Description',
      },
    ]
  }

  if (tableCurrentView === 'metadata') {
    return [
      {
        value: ENRICHMENT_METADATA_TYPES.COLUMN_DESCRIPTION,
        label: 'Descriptions, Business Definitions and Business Rules',
      },
      {
        value: ENRICHMENT_METADATA_TYPES.PII_TAGS,
        label: 'PII Tags',
        disabled: hasEmptyMetadataFields,
      },
      {
        value: ENRICHMENT_METADATA_TYPES.SENSITIVITY_TAGS,
        label: 'Sensitivity Tags',
        disabled: hasEmptyMetadataFields || areAllTaggingFlagsNull(taggingFlags),
      },
    ]
  } else {
    // Data Quality View
    const isDataQualityTagsDisabled =
      hasEmptyMetadataFields || areSensitivityAndDQTagsNull(taggingFlags)
    const isDataQualityRulesDisabled = hasEmptyMetadataFields || isDQTagsNull(taggingFlags)

    return [
      {
        value: ENRICHMENT_METADATA_TYPES.DATA_QUALITY_TAGS,
        label: 'Data Quality Tags',
        disabled: isDataQualityTagsDisabled,
      },
      {
        value: ENRICHMENT_METADATA_TYPES.DQ_RULES,
        label: 'Data Quality Rules',
        disabled: isDataQualityRulesDisabled,
      },
    ]
  }
}

/**
 * Gets enrichment fields based on selected metadata
 */
export const getEnrichmentFields = (selectedMetadata: string[]): string[] => {
  const fields: string[] = []

  if (selectedMetadata?.includes(ENRICHMENT_METADATA_TYPES.TABLE_DESCRIPTION)) {
    fields.push(...ENRICHMENT_FIELD_MAPPINGS.TABLE_DESCRIPTION)
  }
  if (selectedMetadata?.includes(ENRICHMENT_METADATA_TYPES.COLUMN_DESCRIPTION)) {
    fields.push(...ENRICHMENT_FIELD_MAPPINGS.COLUMN_METADATA)
  }
  if (selectedMetadata?.includes(ENRICHMENT_METADATA_TYPES.PII_TAGS)) {
    fields.push(...ENRICHMENT_FIELD_MAPPINGS.PII_TAGS)
  }
  if (selectedMetadata?.includes(ENRICHMENT_METADATA_TYPES.DATA_QUALITY_TAGS)) {
    fields.push(...ENRICHMENT_FIELD_MAPPINGS.DATA_QUALITY_TAGS)
  }
  if (selectedMetadata?.includes(ENRICHMENT_METADATA_TYPES.SENSITIVITY_TAGS)) {
    fields.push(...ENRICHMENT_FIELD_MAPPINGS.SENSITIVITY_TAGS)
  }
  if (selectedMetadata?.includes(ENRICHMENT_METADATA_TYPES.DQ_RULES)) {
    fields.push(...ENRICHMENT_FIELD_MAPPINGS.DQ_RULES)
  }

  return fields
}

/**
 * Gets the default metadata type based on enrichment type and table view
 */
export const getDefaultMetadataType = (
  type: EnrichmentType,
  tableCurrentView: TableViewType = 'metadata',
): EnrichmentMetadataType => {
  if (type === ENRICHMENT_TYPES.TABLE) {
    return ENRICHMENT_METADATA_TYPES.TABLE_DESCRIPTION
  }
  if (type === ENRICHMENT_TYPES.COLUMN && tableCurrentView === 'metadata') {
    return ENRICHMENT_METADATA_TYPES.COLUMN_DESCRIPTION
  }
  if (type === ENRICHMENT_TYPES.COLUMN && tableCurrentView === 'dataQuality') {
    return ENRICHMENT_METADATA_TYPES.DATA_QUALITY_TAGS
  }

  // Fallback to column description
  return ENRICHMENT_METADATA_TYPES.COLUMN_DESCRIPTION
}

// 👇 Field Utilities

/**
 * Creates a unique key for textarea elements
 */
export const createTextareaKey = (columnName: string, fieldName: EditableFieldName): string => {
  return `${columnName}_${fieldName}`
}

/**
 * Checks if a field has meaningful content
 */
const hasContent = (value: string | undefined | null): boolean => {
  return Boolean(value && value.trim() !== '')
}

/**
 * Checks if a column has all required metadata fields filled
 */
const hasCompleteMetadata = (column: ColumnDetail): boolean => {
  return (
    hasContent(column.column_description) &&
    hasContent(column.business_definition) &&
    hasContent(column.business_rules)
  )
}

/**
 * Checks if a column has any metadata field filled
 */
const hasAnyMetadata = (column: ColumnDetail): boolean => {
  return (
    hasContent(column.column_description) ||
    hasContent(column.business_definition) ||
    hasContent(column.business_rules)
  )
}

/**
 * Checks if a column has any tags
 */
const hasAnyTags = (column: ColumnDetail): boolean => {
  const tags = column.tags || { PII_DG: [], DATA_QUALITY_TAGS: [] }
  return flattenTags(tags).length > 0
}

// 👇 Validation Utilities

/**
 * Checks if any column has metadata
 */
export const hasAnyColumnWithMetadata = (columns: ColumnDetail[]): boolean => {
  if (!columns.length) return false
  return columns.some(hasAnyMetadata)
}

/**
 * Checks if any column has tags
 */
export const hasAnyColumnWithTags = (columns: ColumnDetail[]): boolean => {
  if (!columns.length) return false
  return columns.some(hasAnyTags)
}

/**
 * Checks if all columns have complete metadata
 */
export const hasAllColumnsWithCompleteMetadata = (columns: ColumnDetail[]): boolean => {
  if (!columns.length) return false
  return columns.every(hasCompleteMetadata)
}

/**
 * Determines if Clear All button should be disabled for columns
 */
export const shouldDisableClearAllButton = (columns: ColumnDetail[]): boolean => {
  if (!columns.length) return true
  return !hasAnyColumnWithMetadata(columns) && !hasAnyColumnWithTags(columns)
}

/**
 * Determines if Clear All button should be disabled for tables
 */
export const shouldDisableClearAllButtonForTables = (tables: any[]): boolean => {
  if (!tables?.length) return true
  return tables.every((table) => {
    const description = table.tableDescription || table.table_description || ''
    return !hasContent(description)
  })
}

/**
 * Determines if PII/DQ tag options should be disabled
 */
export const shouldDisableTagOptions = (columns: ColumnDetail[]): boolean => {
  if (!columns.length) return true
  return !hasAllColumnsWithCompleteMetadata(columns)
}

// 👇 Formatting Utilities

/**
 * Formats a number as a percentage with rounded value and optional fallback
 * @param value - The numeric value to format (can be undefined)
 * @param fallback - Fallback string when value is undefined (default: "--")
 * @returns Formatted percentage string (e.g., "76%") or fallback
 */
export const formatPercentage = (value: number | undefined, fallback: string = '-'): string => {
  return value !== undefined ? `${Math.round(value)}%` : fallback
}

// 👇 Tag Utilities

/**
 * Flattens tag structure into a simple array of tag strings
 * Handles both snake_case and camelCase formats for backward compatibility
 * Converts SensitivityTagValue to string for unified handling
 */
export const flattenTags = (tags: TagsStructure): string[] => {
  const allTags: string[] = []

  const piiTags = tags.PII_DG || tags.piiDg || []
  const dqTags = tags.DATA_QUALITY_TAGS || tags.dataQualityTags || []
  const sensitivityTags = tags.SENSITIVITY_TAGS || tags.sensitivityTags || []

  if (piiTags) {
    allTags.push(...piiTags.map((item) => item.tag))
  }

  if (dqTags) {
    allTags.push(...dqTags.map((item) => item.tag))
  }

  if (sensitivityTags) {
    allTags.push(...sensitivityTags.map((item) => item.tag))
  }

  return allTags
}

/**
 * Gets color styling for tags based on their category
 * Now works with TagItem[] and category parameter
 */
export const getTagColorByCategory = (
  tag: string,
  category: TagCategory,
  isDisabled?: boolean,
): { bg: string; text: string; iconText: string } => {
  if (category === TAG_CATEGORIES.PII_DG) {
    return {
      bg: 'bg-green-100 text-green-800',
      text: 'text-green-800',
      iconText: isDisabled ? 'text-green-200' : 'text-green-200 hover:text-green-300',
    }
  }

  if (category === TAG_CATEGORIES.DATA_QUALITY_TAGS) {
    return {
      bg: 'bg-mango-75 text-mango-300',
      text: 'text-mango-300',
      iconText: isDisabled ? 'text-mango-200' : 'text-mango-200 hover:text-mango-300',
    }
  }

  return {
    bg: 'bg-gray-100 text-gray-800',
    text: 'text-gray-800',
    iconText: isDisabled ? 'text-gray-400' : 'text-gray-400 hover:text-gray-500',
  }
}

/**
 * Extracts and normalizes PII_DG tags from a tags structure
 * Handles both snake_case and camelCase formats for backward compatibility
 */
export const extractPiiTags = (rawTags: TagsStructure | undefined): TagItem[] => {
  if (!rawTags) {
    return []
  }

  return rawTags.PII_DG || rawTags.piiDg || []
}

/**
 * Extracts and normalizes DATA_QUALITY_TAGS from a tags structure
 * Handles both snake_case and camelCase formats for backward compatibility
 */
export const extractDataQualityTags = (
  rawTags: TagsStructure | undefined,
): DataQualityTagItem[] => {
  if (!rawTags) {
    return []
  }

  return rawTags.DATA_QUALITY_TAGS || rawTags.dataQualityTags || []
}

/**
 * Extracts and normalizes SENSITIVITY_TAGS from a tags structure
 * Handles both snake_case and camelCase formats for backward compatibility
 */
export const extractSensitivityTags = (
  rawTags: TagsStructure | undefined,
): SensitivityTagItem[] => {
  if (!rawTags) {
    return []
  }

  return rawTags.SENSITIVITY_TAGS || rawTags.sensitivityTags || []
}

// TODO: Remove this once we have consistent naming convention

// 👇 Enrichment Data Normalization Utilities

/**
 * Normalizes a DataQualityTagItem from enrichment results
 * Handles both snake_case and camelCase property names
 */
export const normalizeDataQualityTagItem = (item: any): DataQualityTagItem => {
  return {
    tag: item.tag || '',
    description: item.description || '',
    justification: item.justification || '',
    framework_citations: item.framework_citations || item.frameworkCitations || [],
    frameworkCitations: item.framework_citations || item.frameworkCitations || [],
    data_quality_rule: item.data_quality_rule || item.dataQualityRule || '',
    dq_sql_script: item.dq_sql_script || item.dqSqlScript || '',
    execution_result: item.execution_result || item.executionResult || '',
    execution_output: item.execution_output || item.executionOutput || {},
  }
}

/**
 * Normalizes a TagItem from enrichment results
 * Handles both snake_case and camelCase property names
 */
export const normalizeTagItem = (item: any): TagItem => {
  return {
    tag: item.tag || '',
    description: item.description || '',
    justification: item.justification || '',
    framework_citations: item.framework_citations || item.frameworkCitations || [],
    frameworkCitations: item.framework_citations || item.frameworkCitations || [],
  }
}

/**
 * Normalizes a SensitivityTagItem from enrichment results
 * Handles both snake_case and camelCase property names
 */
export const normalizeSensitivityTagItem = (item: any): SensitivityTagItem => {
  const sensitivityTagValues = ['Public', 'Internal', 'Confidential', 'Restricted'] as const
  const validTag = sensitivityTagValues.includes(item.tag) ? item.tag : 'Public'

  return {
    tag: validTag,
    description: item.description,
    justification: item.justification,
    framework_citations: item.framework_citations || item.frameworkCitations,
    frameworkCitations: item.framework_citations || item.frameworkCitations,
  }
}

/**
 * Normalizes a TagsStructure from enrichment results
 * Reuses existing tag extraction functions for consistency
 */
export const normalizeTagsStructure = (tags: any): TagsStructure => {
  if (!tags || typeof tags !== 'object') {
    return { PII_DG: [], DATA_QUALITY_TAGS: [], SENSITIVITY_TAGS: [] }
  }

  const normalizedTags: TagsStructure = {
    PII_DG: [],
    DATA_QUALITY_TAGS: [],
    SENSITIVITY_TAGS: [],
  }

  // Normalize PII_DG tags
  const piiTags = tags.PII_DG || tags.piiDg || []
  if (Array.isArray(piiTags)) {
    normalizedTags.PII_DG = piiTags.map(normalizeTagItem)
  }

  // Normalize DATA_QUALITY_TAGS
  const dqTags = tags.DATA_QUALITY_TAGS || tags.dataQualityTags || []
  if (Array.isArray(dqTags)) {
    normalizedTags.DATA_QUALITY_TAGS = dqTags.map(normalizeDataQualityTagItem)
  }

  // Normalize SENSITIVITY_TAGS
  const sensitivityTags = tags.SENSITIVITY_TAGS || tags.sensitivityTags || []
  if (Array.isArray(sensitivityTags)) {
    normalizedTags.SENSITIVITY_TAGS = sensitivityTags.map(normalizeSensitivityTagItem)
  }

  return normalizedTags
}

/**
 * Normalizes an enriched column from enrichment results
 * Handles both snake_case and camelCase property names
 */
export const normalizeEnrichedColumn = (enrichedCol: any): any => {
  return {
    ...enrichedCol,
    column_name: enrichedCol.column_name || enrichedCol.columnName || '',
    column_description: enrichedCol.column_description || enrichedCol.columnDescription || '',
    business_definition: enrichedCol.business_definition || enrichedCol.businessDefinition || '',
    business_rules: enrichedCol.business_rules || enrichedCol.businessRules || '',
    tags: normalizeTagsStructure(enrichedCol.tags),
  }
}

/**
 * Normalizes an enriched table from enrichment results
 * Handles both snake_case and camelCase property names
 */
export const normalizeEnrichedTable = (enrichedTable: any): any => {
  return {
    ...enrichedTable,
    table_name: enrichedTable.table_name || enrichedTable.tableName || '',
    table_description: enrichedTable.table_description || enrichedTable.tableDescription || '',
    table_domain: enrichedTable.table_domain || enrichedTable.tableDomain || '',
    tags: normalizeTagsStructure(enrichedTable.tags),
    columns:
      enrichedTable.columns && Array.isArray(enrichedTable.columns)
        ? enrichedTable.columns.map(normalizeEnrichedColumn)
        : [],
  }
}
